/**
 * Fetch wrapper for Galley's JSON API (docs/SPEC.md §4).
 *
 * - Same-origin cookies only; the server never enables CORS.
 * - Every non-GET request carries `X-Galley-CSRF` (double-submit token from GET /api/session).
 *   The SessionProvider keeps the token current; if a mutation runs before the session has
 *   loaded, the client fetches it first. A 403 whose code mentions "csrf" refreshes the token
 *   and retries once (the cookie can rotate when a teacher or student signs in).
 * - Failures throw ApiError {status, code, message, details}. status 0 means the network
 *   failed; a non-JSON 5xx means a proxy could not reach the server (code "unavailable").
 * - 401 responses notify `onUnauthorized` listeners (the session provider refetches).
 * - A remote museum (family setup, level 2): after a visit through the Curator's Hub,
 *   `setMuseumTarget` sends `/api/m/*` (and the museum's `/api/assets/*` links) to the
 *   museum's own address with its bearer token instead (see `setMuseumTarget`).
 */
import type { ApiError as ApiErrorBody, HouseholdDTO, SessionDTO } from '@galley/shared'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  /**
   * The message came from Galley's server (plain English, specific to the action). False when
   * we made it up from the HTTP status (a proxy answered, or the body was not ApiError JSON).
   */
  readonly fromServer: boolean

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    options: { fromServer?: boolean } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.fromServer = options.fromServer ?? true
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  /** Extra request headers (an `If-None-Match`, say); Accept, CSRF and auth are set here. */
  headers?: Record<string, string>
  /** The browser's HTTP cache for this request (`no-store`: never turn a 304 into a 200). */
  cache?: RequestCache
  /** Let the request outlive the page (`pagehide`): a small body, sent as the page goes. */
  keepalive?: boolean
}

export const CSRF_HEADER = 'X-Galley-CSRF'

let csrfToken: string | null = null
let csrfRequest: Promise<string | null> | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

type UnauthorizedListener = (error: ApiError) => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

/** Subscribe to 401 responses. Returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => {
    unauthorizedListeners.delete(listener)
  }
}

/** Tell the listeners about a 401 seen outside `api()` (e.g. the live-events stream). */
export function notifyUnauthorized(error: ApiError): void {
  for (const listener of unauthorizedListeners) listener(error)
}

/**
 * A museum reached through the Curator's Hub (docs/family-setup.md §9.5): its API lives at
 * another address and the visitor holds a bearer token from it. The token is kept here, in
 * the page's memory only: never in storage, never in a cookie (a reload means a new visit
 * pass). While a target is set, requests to `/api/m/*` and `/api/assets/*` (and absolute
 * links under the museum's address) go to `base` with `Authorization: Bearer <token>`, no
 * cookies and no CSRF header. Every other request is unchanged.
 */
export interface MuseumTarget {
  /** The museum's origin, e.g. "https://m-4kq7x2.museums.example:8443". */
  base: string
  token: string
  /** Who the museum let in (VisitGrantDTO.household): the museum screens' signed-in person. */
  household?: HouseholdDTO
}

let museumTarget: MuseumTarget | null = null

export function setMuseumTarget(target: MuseumTarget | null): void {
  museumTarget = target
    ? {
        base: target.base.replace(/\/+$/, ''),
        token: target.token,
        ...(target.household && { household: target.household }),
      }
    : null
}

export function getMuseumTarget(): MuseumTarget | null {
  return museumTarget
}

const MUSEUM_PATH = /^\/api\/(?:m|assets)(?:[/?]|$)/

/** The remote museum this request goes to, or null for the usual same-origin request. */
function remoteFor(path: string): MuseumTarget | null {
  const target = museumTarget
  if (!target) return null
  if (MUSEUM_PATH.test(path) || path.startsWith(`${target.base}/`)) return target
  return null
}

/**
 * A file link the museum returned, usable from this page: a relative `/api/assets/…` link
 * gets the remote museum's address in front while a target is set. Anything else (absolute
 * links, other paths, no target) comes back unchanged.
 */
export function museumAssetUrl(url: string): string
export function museumAssetUrl(url: string | null): string | null
export function museumAssetUrl(url: string | null): string | null {
  if (url === null || !museumTarget) return url
  return /^\/api\/assets\//.test(url) ? `${museumTarget.base}${url}` : url
}

/** Build a same-origin URL with an optional query string (skips null/undefined values). */
export function apiUrl(path: string, query?: QueryParams): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined) params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false
  const error = (value as { error: unknown }).error
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { code?: unknown }).code === 'string' &&
    typeof (error as { message?: unknown }).message === 'string'
  )
}

const CODE_BY_STATUS: Record<number, string> = {
  400: 'validation',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  423: 'locked',
  429: 'rate_limited',
  502: 'google',
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/** The ApiError for a failed response (also used by the Curator's Hub client, hub/api.ts). */
export async function toApiError(response: Response): Promise<ApiError> {
  const body = await readBody(response)
  if (isApiErrorBody(body)) {
    return new ApiError(response.status, body.error.code, body.error.message, body.error.details)
  }
  // Galley's server always answers errors with JSON. Anything else came from a proxy or
  // gateway that could not reach it (e.g. the dev proxy while the API is stopped).
  if (response.status >= 500 && (body === null || typeof body === 'string')) {
    return new ApiError(
      response.status,
      'unavailable',
      'Galley’s server could not be reached.',
      body,
      { fromServer: false },
    )
  }
  const code =
    CODE_BY_STATUS[response.status] ?? (response.status >= 500 ? 'server_error' : 'error')
  return new ApiError(response.status, code, response.statusText || 'Request failed', body, {
    fromServer: false,
  })
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

async function ensureCsrf(): Promise<string | null> {
  if (csrfToken) return csrfToken
  csrfRequest ??= send<SessionDTO>('/api/session', {}, false)
    .then((session) => {
      csrfToken = session.csrf
      return session.csrf
    })
    .finally(() => {
      csrfRequest = null
    })
  return csrfRequest
}

/** One request as `api` sends it (remote target, CSRF token), its Response as it came. */
async function request(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { ...options.headers, Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  const remote = remoteFor(path)
  if (remote) {
    // The bearer token alone authenticates: nothing ambient to forge, so no CSRF token.
    headers.Authorization = `Bearer ${remote.token}`
  } else if (method !== 'GET') {
    const token = await ensureCsrf()
    if (token) headers[CSRF_HEADER] = token
  }

  try {
    const url = apiUrl(path, options.query)
    return await fetch(remote && url.startsWith('/') ? `${remote.base}${url}` : url, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: remote ? 'omit' : 'same-origin',
      signal: options.signal,
      ...(options.cache && { cache: options.cache }),
      ...(options.keepalive && { keepalive: true }),
    })
  } catch (error) {
    if (isAbortError(error)) throw error
    throw new ApiError(0, 'network', 'The network request failed.', error)
  }
}

async function send<T>(path: string, options: RequestOptions, retryCsrf: boolean): Promise<T> {
  const method = options.method ?? 'GET'
  const remote = remoteFor(path)
  const response = await request(path, options)

  if (response.ok) {
    if (response.status === 204) return undefined as T
    const body = await readBody(response)
    return body as T
  }

  const error = await toApiError(response)
  if (
    !remote &&
    retryCsrf &&
    method !== 'GET' &&
    error.status === 403 &&
    error.code.includes('csrf')
  ) {
    csrfToken = null
    return send<T>(path, options, false)
  }
  if (error.status === 401) notifyUnauthorized(error)
  throw error
}

/** Call the API. Resolves with the parsed JSON body (undefined for 204). */
export function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send<T>(path, options, true)
}

/** A GET answer with its status and headers: a 304 is an answer too, with no body. */
export interface ApiAnswer<T> {
  status: number
  /** The parsed JSON body; undefined for a 304 or a 204. */
  body: T | undefined
  headers: Headers
}

/**
 * Call the API and keep the answer's status and headers (an ETag, a digest): a conditional GET
 * whose 304 resolves rather than throws. Every other failure throws ApiError, as `api` does.
 */
export async function apiAnswer<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiAnswer<T>> {
  const response = await request(path, options)
  if (response.status === 304 || response.status === 204)
    return { status: response.status, body: undefined, headers: response.headers }
  if (response.ok)
    return {
      status: response.status,
      body: (await readBody(response)) as T,
      headers: response.headers,
    }
  const error = await toApiError(response)
  if (error.status === 401) notifyUnauthorized(error)
  throw error
}
